import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { useI18n } from '../i18n';

export function Button({ variant, size, icon, block, children, className = '', ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'ghost' | 'danger'; size?: 'sm'; icon?: string; block?: boolean }) {
  return (
    <button type="button" className={`btn ${variant ?? ''} ${size ?? ''} ${block ? 'block' : ''} ${className}`} {...rest}>
      {icon && <i className={icon} aria-hidden="true" />}
      {children}
    </button>
  );
}

export function Chip({ tone = 'neutral', children, dot, icon }: { tone?: string; children?: ReactNode; dot?: boolean; icon?: string }) {
  return <span className={`chip ${tone} ${dot ? 'dot' : ''}`}>{icon && <i className={icon} aria-hidden="true" />}{children}</span>;
}

export function Card({ children, pad = true, className = '', tint }: { children: ReactNode; pad?: boolean; className?: string; tint?: 'success' | 'warning' | 'accent' | 'danger' }) {
  return <div className={`card ${pad ? 'pad' : ''} ${tint ? `tint-${tint}` : ''} ${className}`}>{children}</div>;
}

export function Field({ label, value, hint, mono }: { label: string; value: ReactNode; hint?: string; mono?: boolean }) {
  return (
    <div className="field">
      <div className="label">{label}</div>
      <div className={`value ${mono ? 'ref' : ''}`}>{value ?? '—'}</div>
      {hint && <div className="hint">{hint}</div>}
    </div>
  );
}

export function Input({ label, error, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label?: string; error?: string }) {
  return (
    <label className="input">
      {label && <span>{label}</span>}
      <input {...rest} />
      {error && <span className="error">{error}</span>}
    </label>
  );
}

export function Textarea({ label, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: string }) {
  return (
    <label className="input">
      {label && <span>{label}</span>}
      <textarea {...rest} />
    </label>
  );
}

export function Select({ label, options, ...rest }: SelectHTMLAttributes<HTMLSelectElement> & { label?: string; options: Array<{ value: string; label: string }> }) {
  return (
    <label className="input">
      {label && <span>{label}</span>}
      <select {...rest}>{options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
    </label>
  );
}

export function Checkbox({ label, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  return <label className="checkbox"><input type="checkbox" {...rest} /><span>{label}</span></label>;
}

export function StatCard({ label, value, caption, icon, tone }: { label: string; value: string; caption?: string; icon: string; tone?: 'accent' | 'success' | 'danger' }) {
  return (
    <div className="statcard">
      <div className="top"><span className="label">{label}</span><span className={`icon ${tone ?? ''}`}><i className={icon} aria-hidden="true" /></span></div>
      <div><div className="value">{value}</div>{caption && <div className="caption">{caption}</div>}</div>
    </div>
  );
}

export function PageHeader({ crumb, title, sub, actions }: { crumb: string; title: string; sub?: string; actions?: ReactNode }) {
  return (
    <div className="pagehead">
      <div>
        <div className="crumb">{crumb}</div>
        <h1>{title}</h1>
        {sub && <p>{sub}</p>}
      </div>
      {actions && <div className="row">{actions}</div>}
    </div>
  );
}

export function Loading({ error }: { error?: Error | null }) {
  const { t } = useI18n();
  return <div className="loading" style={error ? { color: 'var(--danger)' } : undefined}>{error ? error.message : t('loading')}</div>;
}

export function Empty({ children }: { children?: ReactNode }) {
  const { t } = useI18n();
  return <tr className="empty"><td colSpan={99}>{children ?? t('nothingHere')}</td></tr>;
}

export function Modal({ title, children, onClose }: { title: string; children: ReactNode; onClose: () => void }) {
  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={title}>
        <h2>{title}</h2>
        {children}
      </div>
    </div>
  );
}

/**
 * A yes/no question in front of an action that cannot be taken back. `danger`
 * colours the confirm button and puts the focus on cancel, so Enter cannot
 * destroy anything by accident; `pending` holds both buttons while the action runs.
 */
export function ConfirmDialog({ title, children, confirmLabel, cancelLabel, danger, pending, onConfirm, onClose }: {
  title: string; children: ReactNode; confirmLabel: string; cancelLabel: string; danger?: boolean; pending?: boolean; onConfirm: () => void; onClose: () => void;
}) {
  return (
    <Modal title={title} onClose={() => { if (!pending) onClose(); }}>
      <div className="stack md">
        <div className="secondary" style={{ fontSize: '0.86rem', lineHeight: 1.5 }}>{children}</div>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <Button autoFocus={danger} onClick={onClose} disabled={pending}>{cancelLabel}</Button>
          <Button variant={danger ? 'danger' : 'primary'} autoFocus={!danger} onClick={onConfirm} disabled={pending}>{confirmLabel}</Button>
        </div>
      </div>
    </Modal>
  );
}

export function BackLink({ to, label }: { to: string; label: string }) {
  return <a className="linkbtn" href={to}><i className="fas fa-chevron-left" aria-hidden="true" style={{ fontSize: '0.7rem' }} /> {label}</a>;
}

/** Tone for invoice statuses (matches the demo's chips). */
export function invoiceTone(status: string): string {
  return ({ draft: 'neutral', sent: 'primary', overdue: 'warning', reminder: 'warning', demand_1: 'danger', demand_2: 'danger', collection: 'danger', paid: 'success', cancelled: 'neutral' } as Record<string, string>)[status] ?? 'neutral';
}
export function invoiceStatusLabel(status: string): string {
  return ({ draft: 'Draft', sent: 'Sent', overdue: 'Overdue', reminder: 'Reminder sent', demand_1: 'Demand 1', demand_2: 'Demand 2', collection: 'Collection', paid: 'Paid', cancelled: 'Cancelled' } as Record<string, string>)[status] ?? status;
}
export function vatTreatmentLabel(v: string): string {
  return ({ nl_21: 'NL 21%', nl_9: 'NL 9%', nl_0: 'NL 0%', reverse_charge: 'ICP reverse charge', outside_eu: 'Outside EU', exempt: 'Exempt' } as Record<string, string>)[v] ?? v;
}
export function vatShort(v: string): string {
  return ({ nl_21: '21%', nl_9: '9%', nl_0: '0%', reverse_charge: 'Reverse charge', outside_eu: 'Outside EU', exempt: 'Exempt' } as Record<string, string>)[v] ?? v;
}
export function ticketStatusLabel(s: string): string {
  return ({ new: 'New', in_progress: 'In progress', waiting_on_customer: 'Waiting on customer', resolved: 'Resolved', closed: 'Closed' } as Record<string, string>)[s] ?? s;
}
export function ticketStatusTone(s: string): string {
  return ({ new: 'neutral', in_progress: 'primary', waiting_on_customer: 'warning', resolved: 'success', closed: 'neutral' } as Record<string, string>)[s] ?? 'neutral';
}
export function priorityTone(p: string): string { return ({ high: 'danger', normal: 'primary', low: 'neutral' } as Record<string, string>)[p] ?? 'neutral'; }
export function cap(s: string): string { return s.charAt(0).toUpperCase() + s.slice(1); }
export function mailStatusTone(s: string): string { return ({ queued: 'warning', sent: 'success', delivered: 'success', opened: 'success', failed: 'danger', bounced: 'danger' } as Record<string, string>)[s] ?? 'neutral'; }
export function mailKindLabel(k: string): string { return ({ invoice: 'Invoice', reminder: 'Reminder', demand_1: 'Demand 1', demand_2: 'Demand 2', ticket_reply: 'Ticket reply', portal_invite: 'Portal invite', other: 'Mail' } as Record<string, string>)[k] ?? k; }
export function purchaseInvoiceTone(status: string): string {
  return ({ pending: 'warning', booked: 'success', duplicate: 'danger', ignored: 'neutral' } as Record<string, string>)[status] ?? 'neutral';
}
export function purchaseInvoiceStatusLabel(status: string): string {
  return ({ pending: 'Pending', booked: 'Booked', duplicate: 'Possible duplicate', ignored: 'Ignored' } as Record<string, string>)[status] ?? status;
}
